import { BooleanInput } from '@angular/cdk/coercion';
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, Directive, input } from '@angular/core';
import { cn } from '~/lib/utils';
import {
    RdxDropdownMenuContentDirective,
    RdxDropdownMenuItemCheckboxDirective,
    RdxDropdownMenuItemDirective,
    RdxDropdownMenuItemIndicatorDirective,
    RdxDropdownMenuItemRadioDirective,
    RdxDropdownMenuItemRadioGroupDirective,
    RdxDropdownMenuLabelDirective,
    RdxDropdownMenuSeparatorDirective,
    RdxDropdownMenuTriggerDirective
} from '@radix-ng/primitives/dropdown-menu';
import { Check, ChevronRight, Circle, LucideAngularModule } from 'lucide-angular';

@Directive({
    selector: '[oriDropdownMenuTrigger]',
    hostDirectives: [
        {
            directive: RdxDropdownMenuTriggerDirective,
            inputs: [
                'rdxDropdownMenuTrigger: oriDropdownMenuTrigger',
                'disabled',
                'side',
                'align',
                'sideOffset',
                'alignOffset'
            ],
            outputs: ['onOpenChange']
        }
    ],
    standalone: true
})
export class OriDropdownMenuTrigger {}

@Directive({
    selector: '[oriDropdownMenuContent]',
    hostDirectives: [
        {
            directive: RdxDropdownMenuContentDirective,
            inputs: ['closeOnEscape', 'onEscapeKeyDown']
        }
    ],
    host: {
        '[class]': 'computedClass()'
    },
    standalone: true
})
export class OriDropdownMenuContent {
    readonly class = input<string>();

    readonly computedClass = computed(() =>
        cn(
            'z-50 min-w-40 overflow-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg shadow-black/5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            this.class()
        )
    );
}

// custom directive classes that do no rely on rdxdirective
@Directive({
    selector: '[oriDropdownMenuContent2]',
    host: {
        '[class]': 'computedClass()'
    },
    standalone: true
})
export class OriDropdownMenuContent2 {
    readonly class = input<string>();

    readonly computedClass = computed(() =>
        cn(
            'z-50 min-w-40 overflow-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg shadow-black/5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            this.class()
        )
    );
}



@Directive({
    selector: '[oriDropdownMenuSubContent]',
    hostDirectives: [
        {
            directive: RdxDropdownMenuContentDirective,
            inputs: ['closeOnEscape', 'onEscapeKeyDown']
        }
    ],
    host: {
        '[class]': 'computedClass()'
    },
    standalone: true
})
export class OriDropdownMenuSubContent {
    readonly class = input<string>();

    readonly computedClass = computed(() =>
        cn(
            'z-50 min-w-40 overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg shadow-black/5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            this.class()
        )
    );
}

@Directive({
    selector: '[oriDropdownMenuItem]',
    hostDirectives: [
        {
            directive: RdxDropdownMenuItemDirective,
            inputs: ['disabled'],
            outputs: ['onSelect']
        }
    ],
    host: {
        '[class]': 'computedClass()'
    },
    standalone: true
})
export class OriDropdownMenuItem {
    readonly class = input<string>();
    readonly inset = input<boolean, BooleanInput>(false, { transform: booleanAttribute });

    computedClass = computed(() =>
        cn(
            'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
            this.inset() && 'pl-8',
            this.class()
        )
    );
}

@Directive({
    selector: '[oriDropdownMenuItem2]',
    host: {
        '[class]': 'computedClass()'
    },
    standalone: true
})
export class OriDropdownMenuItem2 {
    readonly class = input<string>();
    readonly inset = input<boolean, BooleanInput>(false, { transform: booleanAttribute });

    computedClass = computed(() =>
        cn(
            'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
            this.inset() && 'pl-8',
            this.class()
        )
    );
}

@Directive({
    selector: '[oriDropdownMenuLabel]',
    hostDirectives: [RdxDropdownMenuLabelDirective],
    host: {
        '[class]': 'computedClass()'
    },
    standalone: true
})
export class OriDropdownMenuLabel {
    readonly class = input<string>();

    readonly inset = input(false, {
        transform: booleanAttribute
    });

    readonly computedClass = computed(() =>
        cn('px-2 py-1.5 text-xs font-medium text-muted-foreground', this.inset() && 'pl-8', this.class())
    );
}

@Directive({
    selector: '[oriDropdownMenuLabel2]',
    host: {
        '[class]': 'computedClass()'
    },
    standalone: true
})
export class OriDropdownMenuLabel2 {
    readonly class = input<string>();

    readonly inset = input(false, {
        transform: booleanAttribute
    });

    readonly computedClass = computed(() =>
        cn('px-2 py-1.5 text-xs font-medium text-muted-foreground', this.inset() && 'pl-8', this.class())
    );
}

@Directive({
    selector: '[oriDropdownMenuSeparator]',
    hostDirectives: [RdxDropdownMenuSeparatorDirective],
    host: {
        '[class]': 'computedClass()'
    },
    standalone: true
})
export class OriDropdownMenuSeparator {
    readonly class = input<string>();

    readonly computedClass = computed(() => cn('-mx-1 my-1 h-px bg-border', this.class()));
}

@Directive({
    selector: 'span[oriDropdownMenuShortcut]',
    host: {
        '[class]': 'computedClass()'
    },
    standalone: true
})
export class OriDropdownMenuShortcut {
    readonly class = input<string>();

    readonly computedClass = computed(() => cn('ml-auto text-xs tracking-widest opacity-60', this.class()));
}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[oriDropdownMenuSubTrigger]',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LucideAngularModule],
    hostDirectives: [
        OriDropdownMenuItem,
        {
            directive: RdxDropdownMenuTriggerDirective,
            inputs: [
                'rdxDropdownMenuTrigger: oriDropdownMenuSubTrigger',
                'disabled',
                'side',
                'align',
                'sideOffset',
                'alignOffset'
            ],
            outputs: ['onOpenChange']
        }
    ],
    host: {
        '[class]': 'computedClass()'
    },
    template: `
        <ng-content />
        <lucide-angular class="text-muted-foreground/80 ml-auto" [img]="ChevronRight" />
    `,
    standalone: true
})
export class OriDropdownMenuSubTrigger {
    readonly class = input<string>();
    readonly inset = input<boolean, BooleanInput>(false, { transform: booleanAttribute });

    computedClass = computed(() =>
        cn(
            'flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
            this.inset() && 'pl-8',
            this.class()
        )
    );

    protected readonly ChevronRight = ChevronRight;
}

@Directive({
    selector: '[oriDropdownMenuRadioGroup]',
    hostDirectives: [
        {
            directive: RdxDropdownMenuItemRadioGroupDirective,
            inputs: ['value'],
            outputs: ['valueChange']
        }
    ]
})
export class OriDropdownMenuRadioGroup {}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[oriDropdownMenuRadioItem]',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LucideAngularModule, RdxDropdownMenuItemIndicatorDirective],
    hostDirectives: [
        {
            directive: RdxDropdownMenuItemRadioDirective,
            inputs: ['value', 'disabled']
        }
    ],
    host: {
        '[class]': 'computedClass()'
    },
    template: `
        <span class="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
            <lucide-angular class="h-2 w-2 fill-current" [img]="Circle" rdxDropdownMenuItemIndicator />
        </span>
        <ng-content></ng-content>
    `,
    standalone: true
})
export class OriDropdownMenuRadioItem {
    readonly class = input<string>();

    readonly computedClass = computed(() =>
        cn(
            'relative flex cursor-default select-none items-center rounded-md py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
            this.class()
        )
    );
    protected readonly Circle = Circle;
}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[oriDropdownMenuCheckboxItem]',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RdxDropdownMenuItemIndicatorDirective, LucideAngularModule],
    hostDirectives: [
        {
            directive: RdxDropdownMenuItemCheckboxDirective,
            inputs: ['checked', 'disabled'],
            outputs: ['checkedChange']
        }
    ],
    host: {
        '[class]': 'computedClass()'
    },
    template: `
        <span class="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
            <lucide-angular class="h-4 w-4" [img]="Check" rdxDropdownMenuItemIndicator />
        </span>
        <ng-content></ng-content>
    `,
    standalone: true
})
export class OriDropdownMenuCheckboxItem {
    readonly class = input<string>();

    readonly computedClass = computed(() =>
        cn(
            'relative flex cursor-default select-none items-center rounded-md py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
            this.class()
        )
    );
    protected readonly Check = Check;
}
